import type { LabeledValue } from 'antd/lib/select';
import type { ColumnsType } from 'antd/lib/table';
import type { Dayjs } from 'dayjs';

import { UserRole } from '../../../enum';
import { useAppSelector } from '../../../hooks';
import type { AdminSite } from '../admin-sites/adminSitesSlice';
import type { CurrentUser } from '../Login/authSlice';
import type { IncompleteSubmissionsData } from '../program-dashboard-olh/programDashboardOLHSlice';
import type {
  DashboardRequestParams,
  Intervention,
  InterventionValue,
  MostCommonWordCount,
  NameTotal,
  NameValue,
} from './programDashboardSlice';
import { BarLineData, InterventionType } from './programDashboardSlice';

export const dateTimeFilterFormat = 'YYYY-MM-DD HH:mm:ss';

export const datesRange: LabeledValue[] = [
  {
    label: '1 Month',
    value: '1 months',
  },
  {
    label: '2 Months',
    value: '2 months',
  },
  {
    label: '3 Months',
    value: '3 months',
  },
];

export const wordsColumns: ColumnsType<MostCommonWordCount> = [
  {
    title: 'Word',
    dataIndex: 'word',
    key: 'word',
    align: 'center',
  },
  {
    title: 'Times used',
    dataIndex: 'frequency',
    key: 'frequency',
    align: 'center',
  },
];

export const interventionColumns: ColumnsType<NameValue> = [
  {
    title: 'Intervention',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'value',
    dataIndex: 'value',
    key: 'value',
  },
];

export const getAdminSiteFilter = (currentUser: CurrentUser, organizationId: string | undefined) => (site: AdminSite) =>
  currentUser.role === UserRole.ADMIN
    ? !organizationId || site.organization_id === organizationId
    : site.organization_id === currentUser.organization_id;

export const useFiltersInfo = ({ start_date, end_date, site_id, organization_id }: Partial<DashboardRequestParams>) => {
  const currentUser = useAppSelector((state) => state.authSlice.currentUser);
  const adminSites = useAppSelector((state) => state.adminSitesSlice.adminSites).filter(
    getAdminSiteFilter(currentUser, organization_id),
  );
  const clinicianSites = useAppSelector((state) => state.adminClinicianSlice.clinicianSites);

  if (!start_date || !end_date) {
    return '';
  }

  const site = site_id
    ? clinicianSites.length > 0
      ? clinicianSites.find((s) => s.site_id === site_id)
      : adminSites.find((s) => s.id === site_id)
    : undefined;

  return `(${start_date.format('YYYY-MM-DD')} - ${end_date.format('YYYY-MM-DD')}${site ? `, ${site.name}` : ''})`;
};

export const getPercentageBarData = (
  totals: NameTotal<'age_group'>[] | NameTotal<'primary_diagnosis'>[] | NameTotal<'gender'>[],
): [BarLineData, BarLineData] => {
  const total = totals.reduce((acc, g) => acc + g.total, 0);
  const percents = totals.map((g) => Math.round((g.total / total) * 1000) / 10);
  const labels = totals.map((g) =>
    'age_group' in g ? g.age_group : 'primary_diagnosis' in g ? g.primary_diagnosis : g.gender,
  );
  return [new BarLineData('Percentage', 'bar', percents), new BarLineData(null, 'category', labels)];
};

export const getBarData = (totals: NameTotal<'military_association'>[]): [BarLineData, BarLineData] => {
  const values = totals.map((t) => t.total);
  const labels = totals.map((g) => g.military_association);
  return [new BarLineData('Count', 'bar', values), new BarLineData(null, 'category', labels)];
};

export const compareDates = (a: Dayjs, b: Dayjs) => {
  if (a.isAfter(b)) {
    return 1;
  } else if (a.isSame(b)) {
    return 0;
  } else {
    return -1;
  }
};

export const getPercentage = (value: number | undefined, total: number) => {
  const rate = (value || 0) / total;

  if (isNaN(rate)) {
    return 0;
  } else {
    return Math.round(rate * 1000) / 10;
  }
};

export const getInterventionsLogged = (interventions: Intervention[]) => {
  let sum = 0;
  const map = interventions.reduce((acc, { intervention_type }) => {
    acc.set(intervention_type, (acc.get(intervention_type) || 0) + 1);
    sum += 1;
    return acc;
  }, new Map<InterventionValue, number>());
  return [
    sum,
    [
      { name: InterventionType.HighSeverity, value: map.get(InterventionType.HighSeverity) || 0 },
      { name: InterventionType.MediumSeverity, value: map.get(InterventionType.MediumSeverity) || 0 },
      { name: InterventionType.LowSeverity, value: map.get(InterventionType.LowSeverity) || 0 },
      { name: InterventionType.FeedbackOnly, value: map.get(InterventionType.FeedbackOnly) || 0 },
    ],
  ] as const;
};

export const getTotalIncompleteSubmissions = (data: IncompleteSubmissionsData | null) => {
  if (!data) return 0;
  const missingPharmaSmartData = data.submissions_without_pharmasmart_data ?? 0;
  const missingQuestions = data.submissions_with_missing_questions ?? 0;
  const missingBoth = data.submissions_missing_both ?? 0;
  return missingQuestions + missingPharmaSmartData - missingBoth;
};
